import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { GoogleLogin } from '@react-oauth/google';
import { API_BASE_URL, buildApiUrl } from '../config/api';
import CustomSelect from './CustomSelect';

import { countries } from '../data/selectOptions';

const SignupForm = () => {
  const { switchToLogin, closeModals, signupData } = useModal();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    gender: '',
    dobDay: '',
    dobMonth: '',
    dobYear: '',
    religion: '',
    country: '',
    agreeToTerms: false,
    is18Plus: false
  });

  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpInput, setOtpInput] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [timer, setTimer] = useState(0);
  const [verificationSuccess, setVerificationSuccess] = useState('');
  const [isGoogleSignup, setIsGoogleSignup] = useState(false);
  const [view, setView] = useState('selection'); // 'selection' or 'form'

  // Pre-fill data if coming from Google (e.g. from Login switch)
  React.useEffect(() => {
    if (signupData) {
      setFormData(prev => ({
        ...prev,
        firstName: signupData.firstName || '',
        lastName: signupData.lastName || '',
        email: signupData.email || '',
        password: prev.password || Math.random().toString(36).slice(-10) + '!',
      }));
      setIsEmailVerified(true);
      setIsGoogleSignup(true);
      setView('form');
    } else {
      // Reset to selection view if no signup data is present
      setView('selection');
      setIsGoogleSignup(false);
      setIsEmailVerified(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        gender: '',
        dobDay: '',
        dobMonth: '',
        dobYear: '',
        religion: '',
        country: '',
        agreeToTerms: false,
        is18Plus: false
      });
    }
  }, [signupData]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: credentialResponse.credential }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.isNewUser) {
          // Pre-fill the form with Google data
          setFormData(prev => ({
            ...prev,
            firstName: data.googleData.firstName || '',
            lastName: data.googleData.lastName || '',
            email: data.googleData.email || '',
            password: prev.password || Math.random().toString(36).slice(-10) + '!',
          }));
          setIsEmailVerified(true);
          setIsGoogleSignup(true);
          setView('form');
        } else {
          // User already exists, log them in
          login(data.user, data.token);
          closeModals();
          window.location.href = '/dashboard';
        }
      } else {
        setError(data.message || 'Google authentication failed');
      }
    } catch (err) {
      console.error('Google Auth Error:', err);
      setError('Failed to connect to Google.');
    } finally {
      setLoading(false);
    }
  };

  const dayOptions = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 100 }, (_, i) => String(currentYear - 18 - i));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  React.useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    if (name === 'email') {
      setIsEmailVerified(false);
      setOtpSent(false);
      setOtpInput(['', '', '', '', '', '']);
      setOtpError('');
    }
  };

  const handleSendOtp = async () => {
    if (!formData.email || !validateEmail(formData.email)) {
      setError('Please enter a valid email address first');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    setError('');

    try {
      const response = await fetch(buildApiUrl('/api/auth/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setOtpSent(true);
        setTimer(60);
        setVerificationSuccess('Verification code sent to your email');
        setTimeout(() => setVerificationSuccess(''), 5000);
      } else {
        setOtpError(data.message || 'Failed to send verification code');
      }
    } catch (err) {
      setOtpError('Error sending verification code');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;

    const newOtpInput = [...otpInput];
    newOtpInput[index] = value;
    setOtpInput(newOtpInput);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpInput[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
        const newOtpInput = [...otpInput];
        newOtpInput[index - 1] = '';
        setOtpInput(newOtpInput);
      }
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData('text').trim().slice(0, 6);
    if (!/^\d+$/.test(pasteData)) return;

    const newOtpInput = [...otpInput];
    pasteData.split('').forEach((char, idx) => {
      if (idx < 6) newOtpInput[idx] = char;
    });
    setOtpInput(newOtpInput);

    // Focus the correct input after paste
    const nextIdx = Math.min(pasteData.length, 5);
    const nextInput = document.getElementById(`otp-${nextIdx}`);
    if (nextInput) nextInput.focus();
  };

  const handleVerifyOtp = async () => {
    const otp = otpInput.join('');
    if (otp.length !== 6) {
      setOtpError('Please enter the 6-digit code');
      return;
    }

    setOtpLoading(true);
    setOtpError('');

    try {
      const response = await fetch(buildApiUrl('/api/auth/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsEmailVerified(true);
        setOtpSent(false);
        setVerificationSuccess('Email verified successfully!');
      } else {
        setOtpError(data.message || 'Invalid or expired code');
      }
    } catch (err) {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isEmailVerified) {
      setError('Please verify your email address first');
      setLoading(false);
      return;
    }

    if (!isGoogleSignup && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!formData.agreeToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      setLoading(false);
      return;
    }

    if (!formData.is18Plus) {
      setError('You must confirm that you are 18 years or older to register');
      setLoading(false);
      return;
    }

    if (!formData.country) {
      setError('Please select your country');
      setLoading(false);
      return;
    }

    if (!formData.dobDay || !formData.dobMonth || !formData.dobYear) {
      setError('Please select your full date of birth');
      setLoading(false);
      return;
    }

    const dobString = `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`;
    const parsedDob = new Date(dobString);
    if (Number.isNaN(parsedDob.getTime())) {
      setError('Please select a valid date of birth');
      setLoading(false);
      return;
    }

    const today = new Date();
    let age = today.getFullYear() - parsedDob.getFullYear();
    const m = today.getMonth() - parsedDob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < parsedDob.getDate())) {
      age--;
    }

    if (age < 18) {
      setError('You must be at least 18 years old to register.');
      setLoading(false);
      return;
    }

    try {
      const signupData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        gender: formData.gender,
        dateOfBirth: dobString,
        religion: formData.religion,
        country: formData.country,
        is18Plus: formData.is18Plus
      };

      const response = await fetch(buildApiUrl('/api/auth/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData),
      });

      const data = await response.json();

      if (response.ok) {
        login(data.user, data.token);
        closeModals();
        window.location.href = '/dashboard';
      } else {
        setError(data.message || 'Signup failed. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check if the server is running.');
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleCountryChange = (selectedCountry) => {
    setFormData({ ...formData, country: selectedCountry });
  };

  return (
    <div className="p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-romantic font-bold text-gray-900 mb-2">Find Your Soulmate</h2>
        <p className="text-gray-600">Create your account in 2 minutes</p>
      </div>

      {view === 'selection' ? (
        <div className="py-10 animate-fade-in">
          <div className="flex flex-col items-center gap-6">
            <div className="w-full max-w-sm">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google signup failed')}
                useOneTap={false}
                theme="filled_blue"
                shape="pill"
                text="signup_with"
                width="100%"
              />
            </div>
            
            <div className="relative w-full max-w-sm my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium italic">or</span>
              </div>
            </div>

            <button
              onClick={() => setView('form')}
              className="w-full max-w-sm py-4 px-6 border-2 border-romantic-200 text-romantic-600 rounded-full font-bold hover:bg-romantic-50 hover:border-romantic-300 transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Sign up with Email
            </button>
          </div>
        </div>
      ) : (
        <div className="animate-slide-up">
          {isGoogleSignup && (
            <div className="mb-6 bg-blue-50 p-4 rounded-lg flex items-center gap-3 text-blue-700 text-sm border border-blue-100 shadow-sm">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="font-medium">Account verified! Please complete your profile details below.</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Form content continues below... */}
        <div className="bg-rose-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
              <input
                type="text" id="firstName" name="firstName" value={formData.firstName}
                onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200"
                placeholder="First name"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
              <input
                type="text" id="lastName" name="lastName" value={formData.lastName}
                onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200"
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
            <div className="flex gap-2">
              <div className="relative flex-grow">
                <input
                  type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                  required disabled={isEmailVerified || otpSent || isGoogleSignup}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200 ${isEmailVerified ? 'bg-green-50 border-green-200' : 'border-gray-300'} ${(isGoogleSignup || otpSent) ? 'opacity-70' : ''}`}
                  placeholder="Enter your email"
                />
                {isEmailVerified && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center text-green-600">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              {!isEmailVerified && !otpSent && (
                <button
                  type="button" onClick={handleSendOtp} disabled={otpLoading || !validateEmail(formData.email)}
                  className="px-4 py-3 bg-romantic-600 text-white rounded-lg font-semibold hover:bg-romantic-700 transition-all disabled:opacity-50 whitespace-nowrap min-w-[120px]"
                >
                  {otpLoading ? '...' : 'Verify Email'}
                </button>
              )}
            </div>

            {otpSent && !isEmailVerified && (
              <div className="mt-4 bg-white p-4 rounded-lg border border-romantic-100 shadow-sm animate-fade-in">
                <div className="bg-sky-50 text-sky-800 p-3 rounded-md mb-4 flex items-start gap-2 text-sm border border-sky-100">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Please check your email inbox (and spam folder) for the 6-digit OTP code to verify your account.</span>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Enter 6-digit verification code:</label>
                <div className="flex justify-between gap-2 mb-4">
                  {otpInput.map((digit, idx) => (
                    <input
                      key={idx} id={`otp-${idx}`} type="text" maxLength="1" value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      onPaste={idx === 0 ? handlePaste : undefined}
                      autoFocus={idx === 0}
                      className="w-10 h-12 text-center text-xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
                    />
                  ))}
                </div>
                {otpError && <p className="text-red-500 text-xs mb-3">{otpError}</p>}
                {verificationSuccess && <p className="text-green-500 text-xs mb-3">{verificationSuccess}</p>}
                <div className="flex items-center justify-between">
                  <button type="button" onClick={handleVerifyOtp} disabled={otpLoading || otpInput.join('').length !== 6} className="bg-romantic-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-romantic-700 disabled:opacity-50">
                    {otpLoading ? 'Verifying...' : 'Verify Code'}
                  </button>
                  <button type="button" onClick={handleSendOtp} disabled={timer > 0 || otpLoading} className="text-sm text-gray-500 hover:text-romantic-600 disabled:opacity-50">
                    {timer > 0 ? `Resend in ${timer}s` : 'Resend code'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-rose-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <input
                type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
                required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter your phone number"
              />
            </div>
            <div>
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">Gender *</label>
              <select
                id="gender" name="gender" value={formData.gender} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              <select
                name="dobDay" value={formData.dobDay} onChange={(e) => handleSelectChange('dobDay', e.target.value)}
                required className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
              >
                <option value="">DD</option>
                {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
              </select>
              <select
                name="dobMonth" value={formData.dobMonth} onChange={(e) => handleSelectChange('dobMonth', e.target.value)}
                required className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
              >
                <option value="">Month</option>
                {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select
                name="dobYear" value={formData.dobYear} onChange={(e) => handleSelectChange('dobYear', e.target.value)}
                required className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent"
              >
                <option value="">Year</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-rose-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Location & Religion</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
              <CustomSelect
                id="country" name="country" value={formData.country} onChange={handleCountryChange}
                options={countries} placeholder="Select Country" required
              />
            </div>
            <div>
              <label htmlFor="religion" className="block text-sm font-medium text-gray-700 mb-2">Religion *</label>
              <select
                id="religion" name="religion" value={formData.religion} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Select Religion</option>
                <option value="Islam">Islam</option>
                <option value="Christianity">Christianity</option>
                <option value="Hinduism">Hinduism</option>
                <option value="Buddhism">Buddhism</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
 
        {!isGoogleSignup && (
          <div className="bg-rose-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Security</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label htmlFor="password" name="password" className="block text-sm font-medium text-gray-700 mb-2">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'} id="password" name="password"
                    value={formData.password} onChange={handleChange} required minLength="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 pr-10"
                    placeholder="Create password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div className="relative">
                <label htmlFor="confirmPassword" name="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword"
                    value={formData.confirmPassword} onChange={handleChange} required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-romantic-500 pr-10"
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox" id="agreeToTerms" name="agreeToTerms" checked={formData.agreeToTerms}
              onChange={handleChange} required className="rounded border-gray-300 text-romantic-600 focus:ring-romantic-500"
            />
            <label htmlFor="agreeToTerms" className="ml-2 text-sm text-gray-600">
              I agree to the <a href="https://marrycenter.com/app/terms" target="_blank" rel="noopener noreferrer" className="text-romantic-600 font-medium">Terms</a> and <a href="https://marrycenter.com/app/privacy" target="_blank" rel="noopener noreferrer" className="text-romantic-600 font-medium">Privacy Policy</a>
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox" id="is18Plus" name="is18Plus" checked={formData.is18Plus}
              onChange={handleChange} required className="rounded border-gray-300 text-romantic-600 focus:ring-romantic-500"
            />
            <label htmlFor="is18Plus" className="ml-2 text-sm text-gray-600">I confirm that I am 18 years or older</label>
          </div>
        </div>

        <button
          type="submit" disabled={loading || !isEmailVerified}
          className="w-full bg-gradient-to-r from-romantic-500 to-primary-500 text-white py-3 px-4 rounded-lg font-semibold hover:opacity-90 transition-all font-romantic disabled:opacity-50"
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mt-4">
          {error}
        </div>
      )}

      <div className="mt-6 text-center">
        <p className="text-gray-600">
          Already have an account? <button onClick={() => switchToLogin()} className="text-romantic-600 font-semibold">Sign in</button>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;